const serviceCodes = [
  "211",
  "311",
  "411",
  "511",
  "611",
  "711",
  "811",
  "911",
  "933",
  "988",
];

export function safeExtensionDigits(value: string): boolean {
  return (
    /^[0-9]{2,8}$/.test(value) &&
    !/^(?:00|01|011|0)$/.test(value) &&
    !serviceCodes.some(
      (code) => value.startsWith(code) || code.startsWith(value),
    )
  );
}

export type DialingResult = {
  kind: "emergency" | "crisis" | "service" | "extension" | "nanp" | "invalid";
  destination: string | null;
  explanation: string;
};

function validNanpNational(value: string): boolean {
  return (
    /^[2-9]\d{2}[2-9]\d{6}$/.test(value) &&
    !/^[2-9]11|^\d{3}[2-9]11/.test(value)
  );
}

/** A local rule check only. This never contacts a PBX or places a call. */
export function checkDialing(
  value: string,
  extensions: string[] = [],
  primaryNumber: string | null = null,
): DialingResult {
  if (value.length > 40 || !/^[+0-9 ().-]+$/.test(value))
    return {
      kind: "invalid",
      destination: null,
      explanation:
        "Enter dialed digits, not a SIP address or dialplan expression.",
    };
  const digits = value.replace(/[ ().-]/g, "");
  if (digits === "911")
    return {
      kind: "emergency",
      destination: "911",
      explanation:
        "Direct emergency dialing. The PBX and carrier must use this device's verified emergency callback number and physical location.",
    };
  if (digits === "988")
    return {
      kind: "crisis",
      destination: "988",
      explanation:
        "Direct dialing to the 988 Suicide & Crisis Lifeline. Do not add an area code or require an outside-line prefix.",
    };
  if (serviceCodes.includes(digits))
    return {
      kind: "service",
      destination: digits,
      explanation:
        "Short service code. Carrier support and the actual outbound route still need verification.",
    };
  if (safeExtensionDigits(digits) && extensions.includes(digits))
    return {
      kind: "extension",
      destination: digits,
      explanation:
        "Matches a saved extension on this account. This does not verify its live PBX destination.",
    };
  if (/^\d{7}$/.test(digits)) {
    const primaryNational =
      typeof primaryNumber === "string" && /^\+1\d{10}$/.test(primaryNumber)
        ? primaryNumber.slice(2)
        : "";
    if (!validNanpNational(primaryNational))
      return {
        kind: "invalid",
        destination: null,
        explanation:
          "Choose a valid primary North American number for this account before using seven-digit dialing. You can still enter the full ten-digit number.",
      };
    const national = `${primaryNational.slice(0, 3)}${digits}`;
    if (validNanpNational(national))
      return {
        kind: "nanp",
        destination: `+1${national}`,
        explanation: `Uses area code ${primaryNational.slice(0, 3)} from this account's saved primary number. This is a format preview; saving a primary number does not confirm that seven-digit dialing is applied to the phone system.`,
      };
    return {
      kind: "invalid",
      destination: null,
      explanation:
        "Use a seven-digit North American number beginning with a valid three-digit exchange, or enter the full ten-digit number.",
    };
  }
  const national = /^\+1/.test(digits)
    ? digits.slice(2)
    : /^1\d{10}$/.test(digits)
      ? digits.slice(1)
      : digits;
  if (validNanpNational(national))
    return {
      kind: "nanp",
      destination: `+1${national}`,
      explanation:
        "Ten-digit North American numbering-plan format. This does not verify the number, calling permissions or price. The connected carrier adapter must format it for its trunk.",
    };
  return {
    kind: "invalid",
    destination: null,
    explanation:
      "Use 911, 988, a saved extension, seven digits with an account primary number, or a ten-digit number with optional 1 or +1. International dialing is not configured.",
  };
}

export function equipmentSlotCount(modelId: string | null): number {
  return modelId === "grandstream-ht802" || modelId === "grandstream-ht802-v2"
    ? 2
    : 1;
}
